import { useMutation } from "@apollo/client";
import { XIcon } from "@heroicons/react/outline";
import { withErrorBoundary } from "@sentry/react";
import { PropsWithChildren, useCallback } from "react";
import {
  DashboardInsightFragment,
  DismissInsightMutationMutation,
  DismissInsightMutationMutationVariables,
  InsightType,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import Avatar from "@components/avatar/avatar";
import Error from "@components/error/error";
import { classNames } from "@helpers/css";
import useConfirm from "@helpers/hooks/use-confirm";

import dismissInsightMutation from "../graphql/dismiss-insight-mutation";
import getDashboardInsightsQuery from "../graphql/get-dashboard-insights-query";
import AssessmentInsight from "./insight/assessment-insight";
import FeedbackRequestInsight from "./insight/feedback-request";
import MissingManagerInsight from "./insight/missing-manager-insight";
import NominatePeersInsight from "./insight/nominate-peers-insight";
import OfftrackGoalInsight from "./insight/offtrack-goal-insight";
import StaleGoalInsight from "./insight/stale-goal-insight";
import SurveyInsight from "./insight/survey-insight";

export const InsightButton = ({
  children,
  url,
  disabled,
  onClick,
  isIcon,
}: PropsWithChildren<{
  url?: string;
  disabled?: boolean;
  isIcon?: boolean;
  onClick?: () => void;
}>) => {
  const CustomTag = url ? "a" : "button";
  return (
    <CustomTag
      className={classNames(
        "text-sm tracking-tight",
        isIcon ? "text-gray-500 hover:text-gray-700" : "text-blue-link",
        disabled ? "text-gray-400 hover:text-gray-400" : "hover:underline",
      )}
      onClick={onClick || undefined}
      href={url || undefined}
      disabled={disabled}
    >
      {children}
    </CustomTag>
  );
};

export const InsightLayout = ({
  user,
  isRequired = false,
  children,
  onDismissInsight,
}: PropsWithChildren<{
  user?: BasicUser;
  isRequired?: boolean;
  onDismissInsight: () => void;
}>) => {
  const { ConfirmationDialog, confirm } = useConfirm(
    "Are you sure?",
    "Are you sure you want to dismiss this insight?",
  );
  const handleDismiss = useCallback(async () => {
    const confirmation = await confirm();
    if (confirmation) {
      onDismissInsight();
    }
  }, [confirm, onDismissInsight]);

  return (
    <div className="text-sm text-gray-800 flex gap-3 relative group">
      <div className="grow-0 shrink-0 h-5">
        <ConfirmationDialog />
        <Avatar user={user} size="5" />
      </div>
      <div className="flex gap-4 flex-1">{children}</div>
      {!isRequired && (
        <div className="flex shrink-0 grow-0 items-start">
          <InsightButton isIcon onClick={handleDismiss}>
            <XIcon className="h-5 w-5" />
          </InsightButton>
        </div>
      )}
    </div>
  );
};

const Insight = ({
  insight,
  selectedUser,
}: {
  insight: DashboardInsightFragment;
  selectedUser: BasicUser;
}) => {
  const [dismissInsight] = useMutation<
    DismissInsightMutationMutation,
    DismissInsightMutationMutationVariables
  >(dismissInsightMutation);

  const handleClickDismissInsight = useCallback(() => {
    dismissInsight({
      variables: {
        userId: selectedUser.id,
        type: insight.type,
        reportId: insight.report?.id || null,
        managerId: insight.manager?.id || null,
        complianceProgramId: insight.complianceProgram?.id || null,
        assessmentGroupId: insight.assessmentGroup?.id || null,
        artifactId:
          (insight.artifact?.__typename === "GoalArtifactNode" &&
            insight.artifact?.id) ||
          null,
      },

      refetchQueries: [getDashboardInsightsQuery],
    });
  }, [dismissInsight, insight, selectedUser]);

  return insight.type === InsightType.MissingManager ? (
    <MissingManagerInsight
      insight={insight}
      selectedUser={selectedUser}
      onDismissInsight={handleClickDismissInsight}
    />
  ) : insight.type === InsightType.OfftrackGoal ? (
    <OfftrackGoalInsight
      selectedUser={selectedUser}
      insight={insight}
      onDismissInsight={handleClickDismissInsight}
    />
  ) : insight.type === InsightType.StaleGoal ? (
    <StaleGoalInsight
      selectedUser={selectedUser}
      insight={insight}
      onDismissInsight={handleClickDismissInsight}
    />
  ) : insight.type === InsightType.CompleteAssessment ? (
    <AssessmentInsight
      selectedUser={selectedUser}
      insight={insight}
      onDismissInsight={handleClickDismissInsight}
    />
  ) : insight.type === InsightType.CompleteSurvey ? (
    <SurveyInsight
      selectedUser={selectedUser}
      insight={insight}
      onDismissInsight={handleClickDismissInsight}
    />
  ) : insight.type === InsightType.NominatePeers ? (
    <NominatePeersInsight
      insight={insight}
      onDismissInsight={handleClickDismissInsight}
    />
  ) : insight.type === InsightType.FeedbackRequest ? (
    <FeedbackRequestInsight
      insight={insight}
      onDismissInsight={handleClickDismissInsight}
    />
  ) : null;
};

const InsightWithSentry = withErrorBoundary(Insight, {
  fallback: <Error description={"This insight could not be displayed."} />,
});

export default InsightWithSentry;
